import { useMemo, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  updateAutomationPromptButtonErrors,
  updateAutomationPromptErrors,
} from 'pages/Automations/state/automationsSlice';
import { useGetFieldErrorText } from 'pages/Automations/hooks/useGetFieldErrorText';

interface UseNextStepValidationInput {
  promptIndex: number;
  buttonIndex: number | undefined;
  isButtonPrompt: boolean;
  skipValidation?: boolean;
}

/**
 * This hook currently exists to:
 *  1. Update next/followup step validation state when a prompt is added or removed; and
 *  2. Return shared next/followup step validation state properties
 *
 * It is a temporary solution until we refactor the automation validation logic to be more centralized.
 */
export const useNextStepValidation = ({
  promptIndex,
  buttonIndex,
  isButtonPrompt,
  skipValidation,
}: UseNextStepValidationInput): {
  fieldNextStepIsInvalid: boolean;
  nextStepErrorText: string;
  fieldFollowupStepIsInvalid: boolean;
  followupStepErrorText: string;
} => {
  const dispatch = useAppDispatch();
  const prompts = useAppSelector((state) => state.automations.automationDraft.prompts);
  const errors = useAppSelector((state) => {
    const promptErrors = state.automations.errors.prompts.find(
      (promptError) => promptError.promptIndex === promptIndex,
    );
    if (isButtonPrompt) {
      return promptErrors?.buttons.find((button) => button.buttonIndex === buttonIndex);
    }
    return promptErrors;
  });
  const { nextStep: nextStepErrors, followupStep: followupStepErrors } = useMemo(
    () => ({ nextStep: errors?.nextStep || [], followupStep: errors?.followupStep || [] }),
    [errors],
  );

  const allButtonErrors = useAppSelector((state) => state.automations.errors.prompts[promptIndex]?.buttons);

  const nextStepErrorText = useGetFieldErrorText(nextStepErrors);
  const followupStepErrorText = useGetFieldErrorText(followupStepErrors);

  const fieldNextStepIsInvalid = useMemo(() => Boolean(nextStepErrors?.length), [nextStepErrors]);
  const fieldFollowupStepIsInvalid = useMemo(() => Boolean(followupStepErrors?.length), [followupStepErrors]);

  // Update next step validation when prompts are added or removed
  useEffect(() => {
    if (skipValidation) {
      return;
    }

    const promptOrder = promptIndex + 1;

    if (fieldNextStepIsInvalid || fieldFollowupStepIsInvalid) {
      if (isButtonPrompt) {
        // Revalidate all buttons in this prompt
        allButtonErrors.forEach((button, index) => {
          if (
            (nextStepErrors.includes('nextPromptMissing') || followupStepErrors.includes('nextPromptMissing')) &&
            promptOrder < prompts.length
          ) {
            dispatch(
              updateAutomationPromptButtonErrors({
                promptIndex,
                buttonIndex: index,
                button: {
                  nextStep: button.nextStep.filter((error) => error !== 'nextPromptMissing'),
                  followupStep: button.followupStep.filter((error) => error !== 'nextPromptMissing'),
                },
              }),
            );
          }

          if (
            (nextStepErrors.includes('unreachablePrompts') || followupStepErrors.includes('unreachablePrompts')) &&
            promptOrder === prompts.length
          ) {
            dispatch(
              updateAutomationPromptButtonErrors({
                promptIndex,
                buttonIndex: index,
                button: {
                  nextStep: button.nextStep.filter((error) => error !== 'unreachablePrompts'),
                  followupStep: button.followupStep.filter((error) => error !== 'unreachablePrompts'),
                },
              }),
            );
          }
        });
      } else {
        if (
          (nextStepErrors?.includes('nextPromptMissing') || followupStepErrors?.includes('nextPromptMissing')) &&
          promptOrder < prompts.length
        ) {
          dispatch(
            updateAutomationPromptErrors({
              promptIndex,
              prompt: {
                nextStep: (nextStepErrors || []).filter((error) => error !== 'nextPromptMissing'),
                followupStep: (followupStepErrors || []).filter((error) => error !== 'nextPromptMissing'),
              },
            }),
          );
        }

        if (
          (nextStepErrors?.includes('unreachablePrompts') ||
            followupStepErrors?.includes('unreachablePrompts') ||
            nextStepErrors?.includes('statementUnreachablePrompts') ||
            followupStepErrors?.includes('statementUnreachablePrompts')) &&
          promptOrder === prompts.length
        ) {
          dispatch(
            updateAutomationPromptErrors({
              promptIndex,
              prompt: {
                nextStep: (nextStepErrors || []).filter(
                  (error) => error !== 'unreachablePrompts' && error !== 'statementUnreachablePrompts',
                ),
                followupStep: (followupStepErrors || []).filter(
                  (error) => error !== 'unreachablePrompts' && error !== 'statementUnreachablePrompts',
                ),
              },
            }),
          );
        }
      }
    }
    // TODO (future PR): Consider moving this to a more central location so components aren't responsible for updating their valdation state
  }, [
    prompts.length,
    dispatch,
    isButtonPrompt,
    nextStepErrors,
    followupStepErrors,
    fieldNextStepIsInvalid,
    fieldFollowupStepIsInvalid,
    allButtonErrors,
    promptIndex,
    skipValidation,
  ]);

  return { fieldNextStepIsInvalid, nextStepErrorText, fieldFollowupStepIsInvalid, followupStepErrorText };
};
